import { get } from 'svelte/store';
import { clone, state, res, ui } from './stores.js';

// 2: choice entries record the resolved action (`act`) instead of the keystroke
// (`shortcut`). Older continuations replay through a branch that no longer
// exists, so the version gate drops them back to the checkpoint.
const CONTINUATION_VERSION = 2;

function continuationId(s){
    return [
        s.gameID || 'local',
        s.turn || 0,
        s.currentPlayer || 0,
        s.phase || 'phase',
        s.step || 'step',
        s.choices && s.choices.thisTurn ? s.choices.thisTurn.length : 0
    ].join(':');
}

function locationFrom(s){
    return {
        turn: s.turn,
        phase: s.phase || null,
        step: s.step || null,
        currentPlayer: s.currentPlayer,
        activePlayer: s.activePlayer
    };
}

function logLengthOf(s){
    return s && s.choices && s.choices.thisTurn ? s.choices.thisTurn.length : 0;
}

function sameStep(a, b){
    if(!a || !b) return false;
    return a.turn === b.turn && (a.phase || null) === (b.phase || null) && (a.step || null) === (b.step || null);
}

function withoutContinuation(s){
    let snapshot = clone(s);
    if(snapshot) delete snapshot.continuation;
    return snapshot;
}

function cleanForJSON(value, depth = 0){
    if(depth > 4) return undefined;
    if(value === undefined || typeof value === 'function') return undefined;
    if(value === null || typeof value !== 'object') return value;
    if(Array.isArray(value)){
        return value.map(v => cleanForJSON(v, depth + 1)).filter(v => v !== undefined);
    }
    if(value.uniqueID){
        return {
            card: value.uniqueID,
            name: value.name,
            id: value.id
        };
    }
    let ret = {};
    for(let key of Object.keys(value)){
        if(['func', 'condition', 'specialCondition', 'liveInfo', 'cardElement'].includes(key)) continue;
        let cleaned = cleanForJSON(value[key], depth + 1);
        if(cleaned !== undefined) ret[key] = cleaned;
    }
    return ret;
}

export function ensureContinuationCheckpoint(label = 'turn-step'){
    let currentState = get(state);
    let currentRes = get(res);
    let existing = currentRes.continuationCheckpoint;
    if(existing && existing.state && existing.loc && existing.loc.turn === currentState.turn && existing.loc.phase === (currentState.phase || null) && existing.loc.step === (currentState.step || null)){
        return existing;
    }

    let checkpointState = currentRes.savedState && Object.keys(currentRes.savedState).length > 0
        ? withoutContinuation(currentRes.savedState)
        : withoutContinuation(currentState);

    let checkpoint = {
        version: CONTINUATION_VERSION,
        entry: 'takeTurns',
        label,
        loc: locationFrom(checkpointState),
        state: checkpointState,
        logLength: logLengthOf(checkpointState),
        createdAt: Date.now()
    };

    res.update(r => {
        r.continuationCheckpoint = checkpoint;
        return r;
    });

    return checkpoint;
}

export function markContinuationCheckpoint(label = 'turn-step'){
    // Continuations only exist to resume a crashed/handed-over game. BvB never
    // resumes, and this clones the whole state on every step of every turn.
    if(get(res).BvB) return;
    let checkpointState = withoutContinuation(get(state));
    let checkpoint = {
        version: CONTINUATION_VERSION,
        entry: 'takeTurns',
        label,
        loc: locationFrom(checkpointState),
        state: checkpointState,
        // Entries already in the log when this baseline was taken. They happened
        // *before* this state, so replay must skip them.
        logLength: logLengthOf(checkpointState),
        createdAt: Date.now()
    };

    res.update(r => {
        r.continuationCheckpoint = checkpoint;
        return r;
    });

    state.update(s => {
        if(s.continuation && s.continuation.status !== 'waiting' && !(get(ui).loadedChoices && get(ui).loadedChoices.length > 0)){
            delete s.continuation;
        }
        return s;
    });
}

export function setContinuationWaiting(kind, wait){
    if(wait && wait.noChoiceLogging) return false;
    let checkpoint = ensureContinuationCheckpoint();
    let currentState = get(state);
    let id = currentState.continuation && currentState.continuation.id
        ? currentState.continuation.id
        : continuationId(currentState);

    state.update(s => {
        s.continuation = {
            version: CONTINUATION_VERSION,
            id,
            entry: checkpoint.entry,
            status: 'waiting',
            loc: locationFrom(s),
            choiceIndex: s.choices && s.choices.thisTurn ? s.choices.thisTurn.length : 0,
            choices: clone(s.choices && s.choices.thisTurn ? s.choices.thisTurn : []),
            pending: {
                kind,
                wait: cleanForJSON(wait)
            },
            updatedAt: Date.now()
        };
        return s;
    });

    return true;
}

export function noteContinuationChoice(){
    state.update(s => {
        if(s.continuation){
            s.continuation.status = 'replaying';
            s.continuation.pending = null;
            s.continuation.choices = clone(s.choices && s.choices.thisTurn ? s.choices.thisTurn : []);
            s.continuation.choiceIndex = s.continuation.choices.length;
            s.continuation.updatedAt = Date.now();
        }
        return s;
    });
}

// The replay log (state.choices.thisTurn) is only meaningful relative to the
// checkpoint it was recorded against. Anything that empties the log must move
// the checkpoint to the same instant, or replay restores an older state and
// re-applies a log that no longer starts there. Do both here so callers can't
// do half of it.
// ponytail: deep-clones the whole state on every save; every caller already
// serialises the full state anyway, so revisit only if profiling says so.
export function resetContinuationBaseline(label = 'baseline'){
    markContinuationCheckpoint(label);
    state.update(s => {
        s.choices.thisTurn = [];
        delete s.continuation;
        return s;
    });
    res.update(r => {
        if(r.continuationCheckpoint) r.continuationCheckpoint.logLength = 0;
        return r;
    });
}

// This runs on *every input*, so it carries only what replay cannot work without:
// the checkpoint to rewind to. It used to send savedState, interimState, undos and
// prevStates as well — five-plus full state copies per click.
//
// The other pieces are still published, once per turn step, by
// extractRelevantPartsOfRes() in net.js, and reactToDB merges rather than replaces,
// so a rejoin still finds savedState/interimState/initialState from that bundle.
export function getContinuationResForSync(){
    let r = get(res);
    return {
        continuationCheckpoint: r.continuationCheckpoint,
        undoThreshold: r.undoThreshold
    };
}

export function prepareContinuationReplay(entry = 'takeTurns'){
    let currentState = get(state);
    let continuation = currentState.continuation;
    if(!continuation || continuation.entry !== entry) return false;
    if(!['waiting', 'replaying'].includes(continuation.status)) return false;
    if(get(ui).waiting && get(ui).waiting.length > 0) return false;
    if(get(ui).continuationReplayId === continuation.id) return false;

    let checkpoint = get(res).continuationCheckpoint;
    let checkpointState = checkpoint && checkpoint.state ? checkpoint.state : get(res).savedState;
    if(!checkpointState || Object.keys(checkpointState).length === 0) return false;

    // Only replay the log if it provably belongs to this checkpoint and this
    // build. Otherwise rewind to the checkpoint with an empty log: that loses
    // at most the current step, where replaying a mismatched log desyncs.
    let checkpointLoc = checkpoint && checkpoint.loc ? checkpoint.loc : locationFrom(checkpointState);
    let logged = continuation.choices || [];
    let skip = checkpoint && typeof checkpoint.logLength === 'number' ? checkpoint.logLength : logLengthOf(checkpointState);
    // A log shorter than its own baseline means something emptied it without
    // re-baselining: the entries replay would need are already gone.
    let trusted = continuation.version === CONTINUATION_VERSION
        && (!checkpoint || checkpoint.version === CONTINUATION_VERSION)
        && sameStep(continuation.loc, checkpointLoc)
        && logged.length >= skip;
    if(!trusted){
        // Never rewind across a step boundary on an untrusted continuation.
        if(!sameStep(checkpointLoc, locationFrom(currentState))) return false;
        console.warn('Continuation not trusted: resuming from checkpoint without replay', {continuationLoc: continuation.loc, checkpointLoc, logged: logged.length, skip});
    }
    let choices = trusted ? clone(logged.slice(skip)) : [];

    let restored = withoutContinuation(checkpointState);
    let liveState = currentState;

    for(let key of ['dbID', 'gameID', 'gameShared', 'advertised', 'hostUsername', 'hostPlayer']){
        if(liveState[key] !== undefined) restored[key] = liveState[key];
    }
    if(restored.players && liveState.players){
        for(let i = 0; i < restored.players.length; i++){
            if(liveState.players[i] && liveState.players[i].username){
                restored.players[i].username = liveState.players[i].username;
            }
        }
    }
    if(!restored.choices){
        restored.choices = {
            thisTurn: [],
            all: [],
            num: 1
        };
    }
    restored.choices.thisTurn = [];
    restored.continuation = {
        ...clone(continuation),
        choices,
        choiceIndex: choices.length,
        status: 'replaying',
        resumedAt: Date.now()
    };

    state.set(restored);
    // The restored log starts empty, so the baseline it is measured against must too.
    res.update(r => {
        if(r.continuationCheckpoint) r.continuationCheckpoint.logLength = 0;
        return r;
    });
    ui.update(u => {
        u.loadedChoices = clone(choices);
        u.catchingUp = choices.length > 0;
        u.continuationReplayId = continuation.id;
        u.waiting = [];
        return u;
    });

    return true;
}
